/**
 * Demo route for the React/Sandpack renderer. Mirrors apps.demo.tsx but uses
 * `render_react_app`-shaped data instead of a spec dashboard. Useful to verify
 * the postMessage data channel and the sandbox boundary end-to-end.
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { AppLayout } from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { ReactAppRenderer } from "@/features/apps/react-app-renderer";
import type { ReactAppSpec } from "@/features/apps/react-app-schema";

export const Route = createFileRoute("/apps/demo-react")({
  head: () => ({
    meta: [
      { title: "React app demo — AskBell" },
      {
        name: "description",
        content: "Sandboxed interactive React app rendered by Sandpack.",
      },
    ],
  }),
  component: DemoReactPage,
});

/* ─────────────── Sample dataset (same shape as spec demo) ─────────────── */
const SAMPLE_ROWS = [
  { region: "Northeast", quarter: "Q1", revenue: 142000, deals: 18 },
  { region: "Northeast", quarter: "Q2", revenue: 175000, deals: 22 },
  { region: "Northeast", quarter: "Q3", revenue: 198000, deals: 25 },
  { region: "Northeast", quarter: "Q4", revenue: 224000, deals: 28 },
  { region: "Southeast", quarter: "Q1", revenue: 98000, deals: 14 },
  { region: "Southeast", quarter: "Q2", revenue: 112000, deals: 16 },
  { region: "Southeast", quarter: "Q3", revenue: 134000, deals: 19 },
  { region: "Southeast", quarter: "Q4", revenue: 156000, deals: 21 },
  { region: "Midwest", quarter: "Q1", revenue: 76000, deals: 11 },
  { region: "Midwest", quarter: "Q2", revenue: 89000, deals: 13 },
  { region: "Midwest", quarter: "Q3", revenue: 102000, deals: 15 },
  { region: "Midwest", quarter: "Q4", revenue: 118000, deals: 17 },
  { region: "West", quarter: "Q1", revenue: 165000, deals: 21 },
  { region: "West", quarter: "Q2", revenue: 188000, deals: 24 },
  { region: "West", quarter: "Q3", revenue: 215000, deals: 27 },
  { region: "West", quarter: "Q4", revenue: 248000, deals: 31 },
];

/* ───────────────── Sample React app ─────────────────
 *
 * A region filter + KPI + bar chart, written exactly as the AI would emit it.
 * Note we read rows via useAskbellData("primary") — never inlined.
 */
const DEMO_APP: ReactAppSpec = {
  version: 1,
  title: "Pipeline explorer",
  description: "Filter quarterly pipeline by region and see live totals.",
  entry: "/App.tsx",
  datasets: [{ dataset_id: "primary", binding: "primary" }],
  files: [
    {
      path: "/App.tsx",
      contents: `import { useMemo, useState } from "react";
import { useAskbellData } from "askbell-data";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

type Row = { region: string; quarter: string; revenue: number; deals: number };

export default function App() {
  const rows = useAskbellData("primary") as Row[];
  const [region, setRegion] = useState<string>("All");

  const regions = useMemo(
    () => ["All", ...Array.from(new Set(rows.map((r) => r.region)))],
    [rows],
  );

  const filtered = region === "All" ? rows : rows.filter((r) => r.region === region);

  const totalRevenue = filtered.reduce((s, r) => s + (Number(r.revenue) || 0), 0);
  const totalDeals = filtered.reduce((s, r) => s + (Number(r.deals) || 0), 0);

  // Group by quarter for the chart.
  const byQuarter = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of filtered) {
      map.set(r.quarter, (map.get(r.quarter) ?? 0) + (Number(r.revenue) || 0));
    }
    return Array.from(map.entries())
      .map(([quarter, revenue]) => ({ quarter, revenue }))
      .sort((a, b) => a.quarter.localeCompare(b.quarter));
  }, [filtered]);

  if (!rows.length) {
    return <div style={{ opacity: 0.7 }}>Waiting for data…</div>;
  }

  return (
    <div>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Pipeline explorer</h1>
        <select
          value={region}
          onChange={(e) => setRegion(e.target.value)}
          style={{
            background: "#0f172a",
            color: "#e2e8f0",
            padding: "6px 10px",
            borderRadius: 6,
            border: "1px solid #334155",
          }}
        >
          {regions.map((r) => (
            <option key={r}>{r}</option>
          ))}
        </select>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
        <Kpi label="Revenue" value={"$" + totalRevenue.toLocaleString()} />
        <Kpi label="Deals" value={totalDeals.toLocaleString()} />
      </div>

      <div style={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, padding: 12, height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={byQuarter}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
            <XAxis dataKey="quarter" stroke="#94a3b8" />
            <YAxis stroke="#94a3b8" />
            <Tooltip contentStyle={{ background: "#0b1220", border: "1px solid #334155" }} />
            <Bar dataKey="revenue" fill="#5eead4" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, padding: 12 }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "#94a3b8" }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>{value}</div>
    </div>
  );
}
`,
      active: true,
    },
  ],
};

function DemoReactPage() {
  return (
    <AppLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-5xl px-6 py-8">
          <Button asChild variant="ghost" size="sm" className="gap-1.5 mb-6 -ml-2">
            <Link to="/apps">
              <ArrowLeft className="h-4 w-4" />
              Back to apps
            </Link>
          </Button>

          <div className="mb-6">
            <h1 className="text-ui-2xl font-bold tracking-tight text-foreground">
              {DEMO_APP.title}
            </h1>
            {DEMO_APP.description && (
              <p className="text-ui-base text-muted-foreground mt-1">{DEMO_APP.description}</p>
            )}
            <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 px-2.5 py-1 text-ui-xs font-bold text-primary">
              <ShieldCheck className="h-3 w-3" />
              Sandboxed (allow-scripts only — no session, no network to your data)
            </div>
          </div>

          <ReactAppRenderer
            spec={DEMO_APP}
            data={{ primary: SAMPLE_ROWS }}
            height={620}
          />
        </div>
      </div>
    </AppLayout>
  );
}
